/**
 * The report's summary strip and its four tables. Pure over ant's numbers:
 * the grouping and the ordering are exported so they are tested on their own.
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDuration } from '../metricsFormat';
import {
  IssueAgentCount,
  IssueCategoryCount,
  IssueMetrics,
  IssueSystemCount,
  IssueTagCount,
} from './useIssueMetrics';

/** How many systems the By system table shows. */
export const TOP_SYSTEMS = 50;

/** The tags ant returns with no glossary category are grouped under this. */
export const OTHER_TAGS_LABEL = 'Other tags';

const numberCell = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

export const formatCount = (n: number): string => n.toLocaleString('en-US');

/** Most first; ties keep ant's order. */
export function sortByTotal<T extends { total: number }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => b.total - a.total);
}

export interface TagGroup {
  categoryId: string | null;
  label: string;
  csChat: number;
  riderApp: number;
  riderAppAuto: number;
  total: number;
  tags: IssueTagCount[];
}

/**
 * The tags under their category, categories most first and tags most first
 * inside each. The group's numbers are the sums of its tags, so they always
 * match the rows under them; tags with no category go under "Other tags",
 * and a category id ant gave no label for is shown as is.
 */
export function groupTagsByCategory(
  tags: IssueTagCount[],
  categories: IssueCategoryCount[]
): TagGroup[] {
  const labels = new Map(categories.map((c) => [c.categoryId, c.label]));
  const groups = new Map<string | null, TagGroup>();
  tags.forEach((tag) => {
    const key = tag.categoryId;
    const group = groups.get(key) ?? {
      categoryId: key,
      label: key === null ? OTHER_TAGS_LABEL : labels.get(key) ?? key,
      csChat: 0,
      riderApp: 0,
      riderAppAuto: 0,
      total: 0,
      tags: [],
    };
    group.csChat += tag.csChat;
    group.riderApp += tag.riderApp;
    group.riderAppAuto += tag.riderAppAuto;
    group.total += tag.total;
    group.tags.push(tag);
    groups.set(key, group);
  });
  return sortByTotal(
    Array.from(groups.values()).map((group) => ({
      ...group,
      tags: sortByTotal(group.tags),
    }))
  );
}

/** The systems with the most issues, at most `limit` of them. */
export function topSystems(
  rows: IssueSystemCount[],
  limit = TOP_SYSTEMS
): IssueSystemCount[] {
  return sortByTotal(rows).slice(0, limit);
}

// ----------------------------------------------------------------------------
// Summary strip
// ----------------------------------------------------------------------------

function SummaryCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: number;
  hint: string;
}) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }} data-testid="issue-summary-card">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Tooltip title={hint} placement="top-start">
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {label}
          </Typography>
        </Tooltip>
        <Typography variant="h5" sx={numberCell}>
          {formatCount(value)}
        </Typography>
      </CardContent>
    </Card>
  );
}

export function SummaryStrip({ metrics }: { metrics: IssueMetrics }) {
  const cards = [
    { label: 'Sessions', value: metrics.sessions, hint: 'Chats opened in the range' },
    {
      label: 'Tagged',
      value: metrics.taggedSessions,
      hint: 'Sessions an agent put at least one tag on',
    },
    { label: 'Untagged', value: metrics.untaggedSessions, hint: 'Sessions with no tag' },
    {
      label: 'Assistant only',
      value: metrics.assistantOnlySessions,
      hint: 'Sessions the assistant handled alone, never a person',
    },
    {
      label: 'Rider reports',
      value: metrics.riderReports,
      hint: 'Problems riders reported in the app in the range',
    },
  ];
  return (
    <Grid container spacing={1.5}>
      {cards.map((card) => (
        <Grid item xs={6} sm={4} md key={card.label}>
          <SummaryCard {...card} />
        </Grid>
      ))}
    </Grid>
  );
}

// ----------------------------------------------------------------------------
// Shared pieces
// ----------------------------------------------------------------------------

function EmptyCard({ title, text }: { title: string; text: string }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardHeader title={title} titleTypographyProps={{ variant: 'subtitle1' }} />
      <CardContent sx={{ pt: 0 }}>
        <Typography variant="body2" color="text.secondary">
          {text}
        </Typography>
      </CardContent>
    </Card>
  );
}

/**
 * The three count columns By category and By tag share. What riders report
 * includes the unlock failures the app records on its own: CS reads them as
 * one thing.
 */
function CountsHead() {
  return (
    <>
      <TableCell align="right">Tagged by CS</TableCell>
      <TableCell align="right">Reported by riders</TableCell>
      <TableCell align="right">Total</TableCell>
    </>
  );
}

function CountCells({
  row,
  bold = false,
}: {
  row: { csChat: number; riderApp: number; riderAppAuto: number; total: number };
  bold?: boolean;
}) {
  const sx = { ...numberCell, fontWeight: bold ? 600 : undefined };
  return (
    <>
      <TableCell align="right" sx={sx}>
        {formatCount(row.csChat)}
      </TableCell>
      <TableCell align="right" sx={sx}>
        {formatCount(row.riderApp + row.riderAppAuto)}
      </TableCell>
      <TableCell align="right" sx={sx}>
        {formatCount(row.total)}
      </TableCell>
    </>
  );
}

// ----------------------------------------------------------------------------
// By category
// ----------------------------------------------------------------------------

export function CategoryTable({ rows }: { rows: IssueCategoryCount[] }) {
  if (rows.length === 0) {
    return (
      <EmptyCard
        title="By category"
        text="No tagged sessions or reports in this range."
      />
    );
  }
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardHeader title="By category" titleTypographyProps={{ variant: 'subtitle1' }} />
      <Table size="small" aria-label="By category">
        <TableHead>
          <TableRow>
            <TableCell>Category</TableCell>
            <CountsHead />
          </TableRow>
        </TableHead>
        <TableBody>
          {sortByTotal(rows).map((row) => (
            <TableRow key={row.categoryId}>
              <TableCell>{row.label}</TableCell>
              <CountCells row={row} />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// By tag, grouped
// ----------------------------------------------------------------------------

function TagGroupRows({ group }: { group: TagGroup }) {
  const [open, setOpen] = useState(true);
  const key = group.categoryId ?? 'other';
  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen((o) => !o)}
        sx={{ cursor: 'pointer', '& > td': { bgcolor: 'action.hover' } }}
        data-testid={`tag-group-${key}`}
      >
        <TableCell sx={{ fontWeight: 600 }}>
          <IconButton
            size="small"
            aria-label={`${open ? 'Collapse' : 'Expand'} ${group.label}`}
            aria-expanded={open}
            sx={{ mr: 0.5, p: 0.25 }}
          >
            {open ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
          {group.label}
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ ml: 1 }}
          >
            {group.tags.length} tag{group.tags.length === 1 ? '' : 's'}
          </Typography>
        </TableCell>
        <CountCells row={group} bold />
      </TableRow>
      {group.tags.map((tag) => (
        <TableRow key={tag.tagId} sx={{ display: open ? undefined : 'none' }}>
          <TableCell sx={{ pl: 5.5 }}>{tag.label}</TableCell>
          <CountCells row={tag} />
        </TableRow>
      ))}
    </>
  );
}

export function TagTable({
  tags,
  categories,
}: {
  tags: IssueTagCount[];
  categories: IssueCategoryCount[];
}) {
  if (tags.length === 0) {
    return (
      <EmptyCard title="By tag" text="No tagged sessions or reports in this range." />
    );
  }
  const groups = groupTagsByCategory(tags, categories);
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardHeader title="By tag" titleTypographyProps={{ variant: 'subtitle1' }} />
      <Table size="small" aria-label="By tag">
        <TableHead>
          <TableRow>
            <TableCell>Tag</TableCell>
            <CountsHead />
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <TagGroupRows key={group.categoryId ?? 'other'} group={group} />
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// By agent
// ----------------------------------------------------------------------------

const formatCsatScore = (csat: number | null): string =>
  csat === null || Number.isNaN(csat) ? '—' : csat.toFixed(2);

export function AgentTable({ rows }: { rows: IssueAgentCount[] }) {
  if (rows.length === 0) {
    return (
      <EmptyCard title="By agent" text="No sessions handled by a person in this range." />
    );
  }
  const sorted = [...rows].sort((a, b) => b.sessionsHandled - a.sessionsHandled);
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardHeader title="By agent" titleTypographyProps={{ variant: 'subtitle1' }} />
      <Table size="small" aria-label="By agent">
        <TableHead>
          <TableRow>
            <TableCell>Agent</TableCell>
            <TableCell align="right">Sessions handled</TableCell>
            <TableCell align="right">Closed</TableCell>
            <TableCell align="right">Tagged</TableCell>
            <TableCell align="right">Median first response</TableCell>
            <TableCell align="right">CSAT</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row) => (
            <TableRow key={row.adminId}>
              <TableCell>{row.name}</TableCell>
              <TableCell align="right" sx={numberCell}>
                {formatCount(row.sessionsHandled)}
              </TableCell>
              <TableCell align="right" sx={numberCell}>
                {formatCount(row.sessionsClosed)}
              </TableCell>
              <TableCell align="right" sx={numberCell}>
                {formatCount(row.sessionsTagged)}
              </TableCell>
              <TableCell align="right" sx={numberCell}>
                {formatDuration(row.medianFirstResponseSeconds)}
              </TableCell>
              <TableCell align="right" sx={numberCell}>
                {formatCsatScore(row.csat)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// By system
// ----------------------------------------------------------------------------

export function SystemTable({ rows }: { rows: IssueSystemCount[] }) {
  if (rows.length === 0) {
    return <EmptyCard title="By system" text="No sessions or reports in this range." />;
  }
  const shown = topSystems(rows);
  const subheader =
    rows.length > shown.length
      ? `Top ${shown.length} of ${formatCount(rows.length)} systems`
      : undefined;
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardHeader
        title="By system"
        subheader={subheader}
        titleTypographyProps={{ variant: 'subtitle1' }}
      />
      <Table size="small" aria-label="By system">
        <TableHead>
          <TableRow>
            <TableCell>System</TableCell>
            <TableCell>Partner</TableCell>
            <TableCell align="right">CS chat</TableCell>
            <TableCell align="right">Rider app</TableCell>
            <TableCell align="right">Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((row) => (
            <TableRow key={row.systemId}>
              <TableCell>{row.name}</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                {row.partnerName ?? '—'}
              </TableCell>
              <TableCell align="right" sx={numberCell}>
                {formatCount(row.csChat)}
              </TableCell>
              <TableCell align="right" sx={numberCell}>
                {formatCount(row.riderApp)}
              </TableCell>
              <TableCell align="right" sx={numberCell}>
                {formatCount(row.total)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
